"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { ContactActions } from "@/components/contact-actions";

/**
 * Contact sidebar. Slides in from the right when the nav "Contact" item is
 * clicked — no navigation, no reload. Dismissible three ways: the close
 * button, the Escape key, or a click on the backdrop. Real contact details
 * only, matching the footer contact block.
 */
export function ContactPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Escape closes; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.button
            aria-label="Close contact panel"
            className="fixed inset-0 z-40 cursor-default"
            style={{ background: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(3px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label="Contact Pisoque Technologies"
            className="glass fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col p-8"
            style={{ background: "var(--surface-solid)" }}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
          >
            <div className="flex items-center justify-between">
              <span className="mono-label text-accent-text">contact</span>
              <motion.button
                onClick={onClose}
                aria-label="Close"
                whileTap={{ scale: 0.9 }}
                className="neo glass inline-flex h-11 w-11 items-center justify-center rounded-xl2 text-lg text-text"
              >
                ✕
              </motion.button>
            </div>

            <h2 className="mt-6 text-2xl font-bold tracking-tight text-text">
              Talk to us
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted">
              Questions about SHERIFF-AI EDR, pricing for your fleet, or a
              deployment for your business — reach Pisoque Technologies directly.
            </p>

            <div className="mt-8">
              <ContactActions />
            </div>

            <p className="mono-label mt-auto text-muted">
              SHERIFF-AI EDR · by Pisoque Technologies
            </p>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
